import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Grid,
  makeStyles,
  useMediaQuery,
} from '@material-ui/core';
import PlanSponsorTitleCard from './components/PlanSponsorTitleCard';
import { FlowCard, LoadingView } from '../../../components';
import AdminReportPlanSummary from './components/PlanSponsorSummary-container';
import PlanSummaryToggle from './components/PlanSummaryToggle';
import { useLocalStorage } from '../../../modules/useLocalStorage';
import { useTranslation } from 'react-i18next';
import ErrorPage from '../../ErrorPage';
import * as AdminReportHelper from '../../../Helper/AdminReportsHelper';
import Session from '../../../modules/Session';
import { HUBBLE_SESSION_AUTH, LOGIN_STATES } from '../../../modules/Login';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(0, 4, 4, 4),
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(4),
      maxWidth: 1080,
      margin: 'auto',
    },
  },
  noBorder: {
    borderRadius: '0 10px 10px 10px',
    marginTop: '-1.75px',
    height: 'fit-content',
  },
  bold: {
    fontWeight: '800',
  },
  grey: {
    color: theme.palette.grey[500],
  },
  button: {
    display: 'block',
  },
  '@media print': {
    page: {
      width: '100%',
      backgroundColor: theme.palette.background.default,
      paddingTop: theme.spacing(4),
      paddingBottom: theme.spacing(6),
    },
    content: {
      width: '100%',
      maxWidth: 1200,
      margin: '0 auto',
      paddingLeft: theme.spacing(3),
      paddingRight: theme.spacing(3),
    },
    button: {
      display: 'none',
    },
  },
}));

const PlanSponsorSummaryReport = () => {
  const classes = useStyles();
  const session = Session.get(HUBBLE_SESSION_AUTH);
  const adminSession = session ? { token: { key: session.token } } : null;
  const { planSponsorGuid } = useParams();
  const { t } = useTranslation('App.Dashboard.PlanSponsorBreakdown');
  const { t: tLoading } = useTranslation('App.Login.Loading');
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('md'));

  const [planSponsorSummaryData, setPlanSponsorSummaryData] = useState(null);
  const [planSponsorSummary, setPlanSponsorSummary] = useState(undefined);
  const [planSponsorInfo, setPlanSponsorInfo] = useState(null);
  const [plan, setPlan] = useState(undefined);
  const [investments, setInvestments] = useState(undefined);
  const [ageRanges, setAgeRanges] = useState(undefined);
  const [error, setError] = useState(null);

  const [isZeroBalanceIncluded, setIsZeroBalanceIncluded] = useLocalStorage(
    'PlanSummaryKey',
    false
  );
  const [
    isTerminatedParticipantsIncluded,
    setIsTerminatedParticipantsIncluded,
  ] = useLocalStorage('PlanSummaryKey1', false);

  const outsideAssets = planSponsorSummary?.outsideAssets;
  const overview = planSponsorSummary?.overview;
  const nonTerminatedNoZeroBalance =
    planSponsorSummary?.nonTerminatedNoZeroBalance;
  const includeTerminated = planSponsorSummary?.includeTerminated;
  const includeZeroBalance = planSponsorSummary?.includeZeroBalance;

  const fetchPlanSponsorSummary = async (planSponsorGuid) => {
    try {
      setError(null);
      const report = await AdminReportHelper.getPlanSponsorSummaryReport(
        adminSession,
        planSponsorGuid
      );
      setPlanSponsorSummaryData(report);
    } catch (err) {
      console.error('Error fetching plan sponsor summary:', err);
      setError(err);
    }
  };

  const clearPlanSponsorSummary = () => {
    setPlanSponsorSummaryData(null);
    setError(null);
  };

  useEffect(() => {
    const initialize = async () => {
      const currentSession = Session.get(HUBBLE_SESSION_AUTH);
      if (
        currentSession &&
        currentSession.loginStatus === LOGIN_STATES.loggedin
      ) {
        fetchPlanSponsorSummary(planSponsorGuid ?? '');
      }
    };

    initialize();
    return () => clearPlanSponsorSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planSponsorGuid]);

  useEffect(() => {
    if (planSponsorSummaryData) {
      setPlanSponsorInfo(planSponsorSummaryData.planSponsorInfo);
      setPlanSponsorSummary(planSponsorSummaryData.planSponsorSummary);
    }
  }, [planSponsorSummaryData]);

  useEffect(() => {
    if (!isZeroBalanceIncluded && !isTerminatedParticipantsIncluded) {
      setPlan(nonTerminatedNoZeroBalance);
      setAgeRanges(nonTerminatedNoZeroBalance?.participantAgeRanges);
      setInvestments(nonTerminatedNoZeroBalance?.participantInvestments);
    } else if (!isZeroBalanceIncluded && isTerminatedParticipantsIncluded) {
      setPlan(includeTerminated);
      setAgeRanges(includeTerminated?.participantAgeRanges);
      setInvestments(includeTerminated?.participantInvestments);
    } else if (isZeroBalanceIncluded && !isTerminatedParticipantsIncluded) {
      setPlan(includeZeroBalance);
      setAgeRanges(includeZeroBalance?.participantAgeRanges);
      setInvestments(includeZeroBalance?.participantInvestments);
    } else {
      setPlan(overview);
      setAgeRanges(overview?.participantAgeRanges);
      setInvestments(overview?.participantInvestments);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isZeroBalanceIncluded,
    isTerminatedParticipantsIncluded,
    nonTerminatedNoZeroBalance,
    includeTerminated,
    includeZeroBalance,
    overview,
  ]);

  if (error) return <ErrorPage />;

  if (!session || session.loginStatus !== LOGIN_STATES.loggedin) {
    return (
      <LoadingView title={tLoading('Title')} message={tLoading('Message')} />
    );
  }

  return (
    <>
      <Box className={classes.container}>
        <Grid
          container
          spacing={4}
          style={{ paddingTop: 32 }}
          justifyContent="left"
          alignItems="left"
          direction="column"
          rowSpacing={isDesktop ? 2 : 4}
        >
          <Grid item>
            <PlanSponsorTitleCard
              isLoading={planSponsorInfo == null}
              planName={planSponsorInfo?.planName}
              provider={planSponsorInfo?.distributor}
              status={planSponsorInfo?.status}
              contractDate={planSponsorInfo?.contractDate}
            />
          </Grid>
          <Grid item style={{ maxWidth: '100%' }}>
            <Grid container justifyContent="flex-end" style={{ width: '100%' }}>
              {isDesktop && (
                <Button
                  className={classes.button}
                  variant="outlined"
                  onClick={() => window.print()}
                  style={{ marginLeft: 16, marginRight: 16, marginBottom: 8 }}
                >
                  {t('Export')}
                </Button>
              )}
            </Grid>
            <FlowCard className={classes.noBorder}>
              <PlanSummaryToggle
                planSponsor={plan}
                isZeroBalance={setIsZeroBalanceIncluded}
                zeroBalance={isZeroBalanceIncluded}
                isTerminatedParticipant={setIsTerminatedParticipantsIncluded}
                terminatedParticipant={isTerminatedParticipantsIncluded}
                lastUpdateDate={planSponsorInfo?.lastUpdateDate}
                outsideAssets={outsideAssets}
              />
              <AdminReportPlanSummary
                participantInvestments={investments}
                participantAgeRanges={ageRanges}
                planSponsorSummaryData={planSponsorSummary}
              />
            </FlowCard>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default PlanSponsorSummaryReport;
